/**
 * „Letzte Aktivität“ (Board „Preisautomatik · Übersicht“) nur aus vorhandenen Daten: der letzte Lauf
 * je Apartment mit seinen Zählern und jeder Warnungscode übersetzt – global oder je Apartment.
 */
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { DynamicPricingApartmentOverview } from '@/shared/api';
import { cn } from '@/shared/lib/cn';

interface Props {
  apartments: readonly DynamicPricingApartmentOverview[];
  globalWarnings: readonly string[];
  when: (iso: string) => string;
}

export function OverviewActivity({ apartments, globalWarnings, when }: Props) {
  const { t } = useTranslation();
  const warning = (code: string) =>
    String(t(`admin.dynamicPricing.overview.warningCodes.${code}`, { defaultValue: code }));
  const global = new Set(globalWarnings);
  const runs = apartments
    .filter((apartment) => apartment.lastRun)
    .sort((a, b) => b.lastRun!.completedAtUtc.localeCompare(a.lastRun!.completedAtUtc));
  const warnings = apartments.flatMap((apartment) =>
    apartment.warningCodes.filter((code) => !global.has(code)).map((code) => ({ apartment, code })),
  );
  const empty = runs.length === 0 && warnings.length === 0 && globalWarnings.length === 0;
  return (
    <section aria-label="Letzte Aktivität" className="bg-card border-hairline rounded-tile border">
      <div className="border-hairline flex items-center gap-2.5 border-b px-5 py-4">
        <h2 className="grow font-serif text-xl font-bold">Letzte Aktivität</h2>
        <Link
          to="/dynamic-pricing/history"
          className="text-forest min-h-11 content-center text-[13px] font-bold"
        >
          Ganzer Verlauf →
        </Link>
      </div>
      {empty && (
        <p className="text-ink-soft px-5 py-4 text-sm">
          Noch keine Berechnungen oder Hinweise in der aktuellen Auswahl.
        </p>
      )}
      {globalWarnings.map((code) => (
        <Item key={code} at="Hinweis" tone="amber" title="Ganzer Workspace" text={warning(code)} />
      ))}
      {warnings.map(({ apartment, code }) => (
        <Item
          key={`${apartment.propertyId}-${code}`}
          at="Hinweis"
          tone="amber"
          title={apartment.name}
          text={warning(code)}
        />
      ))}
      {runs.map((apartment) => {
        const run = apartment.lastRun!;
        return (
          <Item
            key={apartment.propertyId}
            at={when(run.completedAtUtc)}
            tone={apartment.requestCounts.failed > 0 ? 'red' : 'green'}
            title={`${apartment.name} berechnet`}
            text={String(t('admin.dynamicPricing.overview.lastRunCounts', { ...run }))}
          />
        );
      })}
    </section>
  );
}

function Item({
  at,
  tone,
  title,
  text,
}: {
  at: string;
  tone: 'green' | 'amber' | 'red';
  title: string;
  text: string;
}) {
  return (
    <div className="border-card-alt flex items-start gap-3.5 border-b px-5 py-3 last:border-0">
      <span className="text-ink-soft w-[92px] shrink-0 font-mono text-xs">{at}</span>
      <span
        aria-hidden
        className={cn(
          'mt-1.5 size-2.5 shrink-0 rounded-full',
          tone === 'red' ? 'bg-sealwax' : tone === 'amber' ? 'bg-amber' : 'bg-forest',
        )}
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-bold">{title}</span>
        <span className="text-ink-soft text-xs">{text}</span>
      </span>
    </div>
  );
}
